import { Dialog } from "@headlessui/react"
import React, { useEffect, useState } from "react"
import Takedown from "../../../catalog/asset-registry/new-release-action/takedown/Takedown"
import { User, TakedownBatch, UserTakedown, TakedownTrack } from "../../../../../types"
import { NewReleaseActions, TakedownActionTabs } from "../../../../../actions/types"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { users } from "../../../../../apis"
import { formatNumber, getCurrency, mapListToCamelCase, mapToCamelCase } from "../../../../../utility"
import Loading from "../../../../ui/Loading"
import { toast } from "react-toastify"
import { errorFilledToastOptions } from "../../../../../constants"

const columnWidths = {
  ALBUM_TITLE: "20%",
  ALBUM_UPC: "11%",
  ALBUM_DISTRIBUTOR: "20%",
  TRACK_TITLE: "20%",
  TRACK_ISRC: "11%",
  OFFER_LTM: "9%",
  ADVANCE_OFFER: "9%"
}

const AdminTakedownContractModal = ({
  selectedContractTakedown,
  handleClose
}: {
  selectedContractTakedown?: UserTakedown
  handleClose: () => void
}) => {
  const [takedownBatch, setTakedownBatch] = useState<TakedownBatch>()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!selectedContractTakedown) {
      return
    }
    if (!selectedContractTakedown.confirmedOfferId) {
      toast.error("Confirmed Offer ID missing. Create +User Takedown with Confirmed Offer ID", errorFilledToastOptions)
      handleClose()
      return
    }
    setLoading(true)
    users(
      `/list_takedown_batch_list/?album_ids=${selectedContractTakedown.albumIds}&confirmed_offer_id=${selectedContractTakedown.confirmedOfferId}`,
      "GET"
    )
      .then((resp) => {
        const takedownBatch: TakedownBatch = mapToCamelCase(resp.data)
        setTakedownBatch(takedownBatch)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        toast.error("Error fetching takedown batch", errorFilledToastOptions)
      })
  }, [selectedContractTakedown])

  const renderTakedownBatches = () => {
    return (
      <div className="mt-8">
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Platform Royalties" fontSize={16} />
          <div className="flex gap-8">
            {Object.entries(takedownBatch?.platformRoyalties || {})?.map(([platform, value]) => {
              return (
                <div>
                  <KSpan text={`${platform}: `} />
                  <KSpan text={`${formatNumber(value, 2)} ${getCurrency(takedownBatch?.currency || "")}`} />
                </div>
              )
            })}
          </div>
        </div>
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Countries Royalties" fontSize={16} />
          <div className="flex gap-8">
            {Object.entries(takedownBatch?.countryRoyalties || {})?.map(([country, value]) => {
              return (
                <div>
                  <KSpan text={`${country.toUpperCase()}: `} />
                  <KSpan text={`${formatNumber(value, 2)} ${getCurrency(takedownBatch?.currency || "")}`} />
                </div>
              )
            })}
          </div>
        </div>

        <div>
          <div style={{ minWidth: "100%" }} className="flex mt-10">
            {Object.entries(columnWidths).map(([header, width]) => {
              return (
                <div style={{ width }} key={`header_${header}`}>
                  <KSpan color="#111" text={header} fontWeight={500} />
                </div>
              )
            })}
          </div>
        </div>
        {takedownBatch?.trackList?.map((takedownTrack: TakedownTrack) => {
          const album = takedownTrack.album
          const offer = takedownTrack.songSelection

          return (
            <div
              key={`track_${takedownTrack.id}`}
              className="mt-3 mb-3 pb-3 flex gap-2"
              style={{ borderTop: "1px dashed gray", width: "100%" }}
            >
              <div style={{ width: columnWidths.ALBUM_TITLE }}>
                <KSpan fontWeight={500} text={`${album?.title || "-"}`} />
              </div>
              <div style={{ width: columnWidths.ALBUM_UPC }}>
                <KSpan fontWeight={500} text={`${album?.upc || "-"}`} />
              </div>
              <div style={{ width: columnWidths.ALBUM_DISTRIBUTOR }}>
                <KSpan fontWeight={500} text={`${album?.distributor || "-"}`} />
              </div>
              <div style={{ width: columnWidths.TRACK_TITLE }}>
                <KSpan fontWeight={500} text={`${takedownTrack.title || "-"}`} />
              </div>
              <div style={{ width: columnWidths.TRACK_ISRC }}>
                <KSpan fontWeight={500} text={`${takedownTrack.isrc || "-"}`} />
              </div>
              <div style={{ width: columnWidths.OFFER_LTM }}>
                <KSpan
                  fontWeight={500}
                  text={`${formatNumber(offer.ltmIncome || "0", 2)} ${getCurrency(takedownBatch.currency)}`}
                />
              </div>
              <div style={{ width: columnWidths.ADVANCE_OFFER }}>
                <KSpan
                  fontWeight={500}
                  text={`${formatNumber(offer.advanceOffer || "0", 2)} ${getCurrency(takedownBatch.currency)}`}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Dialog open={selectedContractTakedown !== undefined} onClose={() => handleClose()}>
      <div className="fixed w-screen h-screen top-0 left-0 z-250 flex items-center justify-center bg-[#0000004d] ">
        <div
          className="p-6 shadow-md rounded-[10px] bg-[#fff]"
          style={{ height: "90vh", overflowY: "scroll", minWidth: 1200 }}
        >
          <div>
            <KTitleSpan text="Takedown Batch Based on Distributor and Revenue" fontSize={24} />
            <KSpan
              text={`Selected User: ${selectedContractTakedown?.user?.firstName} ${selectedContractTakedown?.user?.lastName}`}
            />
            {loading && <Loading />}
            {renderTakedownBatches()}
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default AdminTakedownContractModal
